import React from 'react';
import { getDurationInHoursAndMinutes } from '@teetsh/app/src/utils/time/getDurationInHoursAndMinutes';
import { bgColors } from '@teetsh/app/src/components/colors';
import tw from 'twin.macro';
import { Color } from '@teetsh/app/src/types/colors';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

export interface Item {
  id?: string;
  minutes: number;
  name?: string;
  color?: Color;
}

interface Group {
  id: string;
  name: string;
  items: Item[];
}

interface Props {
  groups: Group[];
  children?: any;
}

function getTotalDuration(tags: Item[]): number {
  return tags.reduce((acc, tag) => {
    const { minutes } = tag;
    return acc + minutes;
  }, 0);
}

function getItemColor(item: Item): Color {
  const { color } = item;
  if (!color) {
    return 'gray-400';
  }
  return color;
}

export default function RepartitionHoraire(props: Props) {
  const { groups } = props;
  const { t } = useTranslation(['common']);
  if (groups.length === 0) {
    return (
      <div tw="pb-2">{t('common:components.repartitionHoraire.noItem')}</div>
    );
  }
  return (
    <>
      <div tw="space-y-4">
        {groups.map(group => (
          <div key={group.id}>
            {groups.length > 1 && (
              <div
                css={[tw`my-0 mr-2 font-bold tracking-tight mb-4 text-base`]}
              >
                {group.name}
              </div>
            )}
            <div tw="space-y-2">
              {group.items.map(item => (
                <div key={item.id || 'unknown'} css={[tw`mr-2`]}>
                  <div css={[tw`flex justify-between font-normal text-sm`]}>
                    <span tw="truncate">
                      {item.name ||
                        t(
                          'common:components.repartitionHoraire.defaultTagName',
                        )}
                    </span>
                  </div>
                  <div tw="flex space-x-2 items-center w-full">
                    <div css={[tw`relative flex-grow h-3`]} tw="relative">
                      <div
                        css={[
                          tw`w-full rounded top-0 left-0 bottom-0 z-10 absolute`,
                          bgColors(getItemColor(item)),
                          css`
                            width: ${(100 * item.minutes) /
                            getTotalDuration(group.items)}%;
                          `,
                        ]}
                      />
                      <div
                        css={[
                          tw`w-full rounded bg-neutral-supershy absolute top-0 left-0 right-0 bottom-0 h-3`,
                        ]}
                      />
                    </div>
                    <span
                      css={[
                        tw`font-semibold text-sm w-14 shrink-0 text-right text-neutral-moderate`,
                      ]}
                    >
                      {getDurationInHoursAndMinutes(item.minutes)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {props.children ? (
              props.children({
                total: getDurationInHoursAndMinutes(
                  getTotalDuration(group.items),
                ),
              })
            ) : (
              <div tw="flex justify-between mt-4">
                <div tw="text-muted">
                  {t('common:components.repartitionHoraire.total')}
                </div>
                <div css={[tw`font-bold text-base`]}>
                  {getDurationInHoursAndMinutes(getTotalDuration(group.items))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
